import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Cookies from "js-cookie";
import { Hub } from "aws-amplify/utils";
import { fetchAuthSession } from "aws-amplify/auth";
import AccountsAPI from "../services/AccountsAPI";
import { getSubscriptionState } from "../utility/subscriptionAccess";

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const emptyAuthState = {
  jwt: null,
  organizationId: null,
  organizationName: null,
  userName: null,
  subscriptionStatus: null,
  subscriptionSource: null,
  subscriptionEndsAt: null,
  subscriptionRenews: false,
  subscriptionCancelAtPeriodEnd: false,
  subscriptionAutoRenew: false,
  hasActiveSubscription: false,
  educatorTrialExpiresAt: null,
  subscriptionPurchaseType: null,
  subscriptionBillingInterval: null,
  billingProducts: [],
  isAuthenticated: false,
  isLoading: true,
  isAccountProfileLoading: false,
  userId: null,
  roleId: null,
  roleName: null,
  permissions: {},
};

const getApiResponseData = (response) => response?.data?.data ?? response?.data ?? null;

const fetchAccountProfile = async (userId) => {
  if (!userId) {
    return null;
  }

  try {
    const response = await AccountsAPI.get(`/users/${userId}`, {
      requiresAuth: true,
    });
    return getApiResponseData(response);
  } catch (error) {
    console.error("Error fetching account profile:", error);
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState(emptyAuthState);
  const attemptedClaimRefreshesRef = useRef(new Set());
  const lastForegroundSyncAtRef = useRef(0);
  const authRequestVersionRef = useRef(0);

  const checkCurrentUser = useCallback(async ({ forceRefresh = false } = {}) => {
    const requestVersion = ++authRequestVersionRef.current;
    try {
      const session = await fetchAuthSession({ forceRefresh });
      if (requestVersion !== authRequestVersionRef.current) return;

      if (!session.userSub || !session.tokens?.accessToken) {
        setAuthState({
          ...emptyAuthState,
          isLoading: false,
        });
        Cookies.remove("accessToken", { domain: ".10k.science" });
        return;
      }

      const idToken = session.tokens.idToken;
      const accessToken = session.tokens.accessToken;
      const accessTokenPayload = accessToken.payload || {};
      const idTokenPayload = idToken?.payload || {};
      const userId =
        accessTokenPayload.userId ||
        idTokenPayload["custom:userId"] ||
        session.userSub ||
        null;
      const subscriptionState = getSubscriptionState({
        status: accessTokenPayload.subscriptionStatus,
        source: accessTokenPayload.subscriptionSource,
        expirationDate: accessTokenPayload.subscriptionExpirationDate,
        renews: accessTokenPayload.subscriptionRenews,
        autoRenew: accessTokenPayload.subscriptionAutoRenew,
        cancelAtPeriodEnd: accessTokenPayload.subscriptionCancelAtPeriodEnd,
      });

      const jwt = accessToken.toString();

      Cookies.set("accessToken", jwt, {
        domain: ".10k.science",
        secure: true,
        sameSite: "Lax",
        expires: 1,
      });

      const nextAuthState = {
        jwt,
        organizationId:
          accessTokenPayload.organizationId ||
          idTokenPayload["custom:organizationId"] ||
          null,
        organizationName: idTokenPayload["custom:organizationName"] || null,
        userName:
          idTokenPayload.name ||
          [idTokenPayload.given_name, idTokenPayload.family_name]
            .filter(Boolean)
            .join(" ")
            .trim() ||
          idTokenPayload.email ||
          null,
        subscriptionStatus: subscriptionState.status,
        subscriptionSource: subscriptionState.source,
        subscriptionEndsAt: subscriptionState.expirationDate,
        subscriptionRenews: subscriptionState.renews,
        subscriptionCancelAtPeriodEnd: subscriptionState.cancelAtPeriodEnd,
        subscriptionAutoRenew: subscriptionState.autoRenew,
        hasActiveSubscription: subscriptionState.hasActiveSubscription,
        isAuthenticated: true,
        isLoading: false,
        isAccountProfileLoading: Boolean(userId),
        userId,
        roleId: accessTokenPayload.roleId || null,
        roleName: accessTokenPayload.roleName || null,
        permissions: accessTokenPayload.permissions || {},
        educatorTrialExpiresAt: accessTokenPayload.educatorTrialExpiresAt || null,
        subscriptionPurchaseType: accessTokenPayload.subscriptionPurchaseType || null,
        subscriptionBillingInterval: accessTokenPayload.subscriptionBillingInterval || null,
        billingProducts: [],
      };

      setAuthState(nextAuthState);

      if (userId) {
        void fetchAccountProfile(userId).then((profile) => {
          if (requestVersion !== authRequestVersionRef.current) return;
          const hasProfileSubscription = Boolean(profile?.subscription);
          const profileSubscriptionState = getSubscriptionState({
            status: profile?.subscription?.type,
            source: profile?.subscription?.source,
            expirationDate: profile?.subscription?.expirationDate,
            renews: profile?.subscription?.renews,
            autoRenew: profile?.subscription?.autoRenew,
            cancelAtPeriodEnd: profile?.subscription?.cancelAtPeriodEnd,
          });
          const shouldRefreshClaims =
            Boolean(profile) &&
            (
              (profile?.roleId && profile.roleId !== nextAuthState.roleId) ||
              (profile?.roleName && profile.roleName !== nextAuthState.roleName) ||
              (profile?.educatorTrialExpiresAt || null) !== nextAuthState.educatorTrialExpiresAt ||
              (profile?.subscription?.purchaseType || null) !== nextAuthState.subscriptionPurchaseType ||
              (profile?.subscription?.billingInterval || null) !== nextAuthState.subscriptionBillingInterval ||
              JSON.stringify(profile?.permissions || {}) !== JSON.stringify(nextAuthState.permissions) ||
              profileSubscriptionState.status !== nextAuthState.subscriptionStatus ||
              profileSubscriptionState.source !== nextAuthState.subscriptionSource ||
              profileSubscriptionState.hasActiveSubscription !==
                nextAuthState.hasActiveSubscription ||
              String(profileSubscriptionState.expirationDate || "") !==
                String(nextAuthState.subscriptionEndsAt || "") ||
              profileSubscriptionState.renews !==
                nextAuthState.subscriptionRenews ||
              profileSubscriptionState.autoRenew !==
                nextAuthState.subscriptionAutoRenew ||
              profileSubscriptionState.cancelAtPeriodEnd !==
                nextAuthState.subscriptionCancelAtPeriodEnd
            );
          const refreshSignature = JSON.stringify({
            userId,
            roleId: profile?.roleId || null,
            roleName: profile?.roleName || null,
            permissions: profile?.permissions || {},
            educatorTrialExpiresAt: profile?.educatorTrialExpiresAt || null,
            purchaseType: profile?.subscription?.purchaseType || null,
            billingInterval: profile?.subscription?.billingInterval || null,
            subscriptionStatus: profileSubscriptionState.status,
            subscriptionSource: profileSubscriptionState.source,
            expirationDate: profileSubscriptionState.expirationDate
              ? profileSubscriptionState.expirationDate.toISOString()
              : null,
            renews: profileSubscriptionState.renews,
            autoRenew: profileSubscriptionState.autoRenew,
            cancelAtPeriodEnd: profileSubscriptionState.cancelAtPeriodEnd,
          });

          if (!shouldRefreshClaims) {
            attemptedClaimRefreshesRef.current.delete(refreshSignature);
          }

          setAuthState((currentAuthState) => {
            if (
              !currentAuthState.isAuthenticated ||
              currentAuthState.userId !== userId
            ) {
              return currentAuthState;
            }

            return {
              ...currentAuthState,
              isAccountProfileLoading: false,
              roleId: profile?.roleId || currentAuthState.roleId,
              roleName: profile?.roleName || currentAuthState.roleName,
              ...(profile ? {
                permissions: profile.permissions || {},
                educatorTrialExpiresAt: profile.educatorTrialExpiresAt || null,
                billingProducts: Array.isArray(profile.billingProducts) ? profile.billingProducts : [],
                subscriptionPurchaseType: profile.subscription?.purchaseType || null,
                subscriptionBillingInterval: profile.subscription?.billingInterval || null,
              } : {}),
              ...(hasProfileSubscription
                ? {
                    subscriptionStatus: profileSubscriptionState.status,
                    subscriptionSource: profileSubscriptionState.source,
                    subscriptionEndsAt: profileSubscriptionState.expirationDate,
                    subscriptionRenews: profileSubscriptionState.renews,
                    subscriptionCancelAtPeriodEnd:
                      profileSubscriptionState.cancelAtPeriodEnd,
                    subscriptionAutoRenew: profileSubscriptionState.autoRenew,
                    hasActiveSubscription: profileSubscriptionState.hasActiveSubscription,
                  }
                : {}),
            };
          });

          if (
            shouldRefreshClaims &&
            !attemptedClaimRefreshesRef.current.has(refreshSignature)
          ) {
            attemptedClaimRefreshesRef.current.add(refreshSignature);
            void checkCurrentUser({ forceRefresh: true });
          }
        });
      }
    } catch (error) {
      if (requestVersion !== authRequestVersionRef.current) return;
      console.log("Error fetching user session:", error);
      setAuthState({
        ...emptyAuthState,
        isLoading: false,
      });
    }
  }, []);

  const refreshAuthSession = useCallback(() => {
    return checkCurrentUser({ forceRefresh: true });
  }, [checkCurrentUser]);

  useEffect(() => {
    if (!authState.isAuthenticated) return undefined;
    const nextExpiry = [authState.subscriptionEndsAt, authState.educatorTrialExpiresAt]
      .filter(Boolean).map((date) => new Date(date).getTime())
      .filter((time) => Number.isFinite(time) && time > Date.now())
      .sort((a, b) => a - b)[0];
    if (!nextExpiry) return undefined;
    const timer = window.setTimeout(() => {
      setAuthState((current) => ({ ...current, permissions: {} }));
      void checkCurrentUser({ forceRefresh: true });
    }, Math.min(nextExpiry - Date.now() + 1, 2_147_000_000));
    return () => window.clearTimeout(timer);
  }, [authState.isAuthenticated, authState.subscriptionEndsAt, authState.educatorTrialExpiresAt, checkCurrentUser]);

  const syncVisibleSession = useCallback(() => {
    const now = Date.now();

    if (now - lastForegroundSyncAtRef.current < 1000) {
      return;
    }

    lastForegroundSyncAtRef.current = now;
    void checkCurrentUser();
  }, [checkCurrentUser]);

  useEffect(() => {
    const cancelAuthListener = Hub.listen("auth", ({ payload: { event } }) => {
      if (["signedIn", "signedOut", "tokenRefresh"].includes(event)) {
        checkCurrentUser();
      }
    });

    checkCurrentUser();

    return () => {
      if (typeof cancelAuthListener === "function") {
        cancelAuthListener();
      }
    };
  }, [checkCurrentUser]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncVisibleSession();
      }
    };

    window.addEventListener("focus", syncVisibleSession);
    window.addEventListener("pageshow", syncVisibleSession);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", syncVisibleSession);
      window.removeEventListener("pageshow", syncVisibleSession);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [syncVisibleSession]);

  const value = useMemo(
    () => ({
      ...authState,
      refreshAuthSession,
    }),
    [authState, refreshAuthSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
